/**
 * La política de consentimiento de las campañas, en un módulo PURO.
 *
 * Vive aparte y sin dependencias para que se lea de un vistazo y se cambie en UN sitio.
 * Es deliberado que aquí no haya Firestore, ni red, ni logs: lo que decide quién puede
 * recibir qué es una regla de negocio, y tiene que poder revisarse sin leer el motor.
 *
 * SE APLICA SOBRE HECHOS, NO SOBRE VEREDICTOS. El modelo guarda dos cosas distintas
 * (ver `messageRouter.js`): `primerMensajeEntrante` es lo que PASÓ —con el `wamid` de
 * Meta como evidencia— y `consentimiento` es lo que la PERSONA DECIDIÓ. Ninguno de los
 * dos es un `optIn: true`. La conclusión de si se puede enviar se calcula AQUÍ, en el
 * momento del envío, y si la interpretación cambia se cambia esta función y no miles
 * de registros.
 *
 * LA POLÍTICA
 * ───────────
 *   UTILITY        permitido si consta que la persona escribió al negocio
 *   MARKETING      exige consentimiento explícito
 *   AUTHENTICATION NO enviable desde campañas, ni con consentimiento
 *   desconocida    bloqueada
 *
 * POR QUÉ AUTHENTICATION NO SE GATEA CON CONSENTIMIENTO SINO QUE SE PROHÍBE. Una
 * plantilla de autenticación es un código de un solo uso: transaccional, uno a uno,
 * disparado por un acto del usuario en ese mismo instante. Un envío MASIVO de códigos
 * de verificación no es un caso de uso legítimo — o es un error de configuración, o es
 * un ataque. Bloquearla por consentimiento habría dejado que un cliente con
 * consentimiento explícito lanzara mil OTPs; así no puede aunque quiera.
 *
 * ESTA REGLA ES MÁS PERMISIVA QUE LA LETRA DE META, y es una decisión consciente, no
 * un descuido. Meta exige opt-in antes de enviar plantillas; desde noviembre de 2024
 * ese opt-in puede ser general —no necesita mencionar WhatsApp— pero debe declarar que
 * la persona acepta recibir comunicaciones y nombrar al negocio. Que alguien escriba
 * al negocio le da su NÚMERO, no su PERMISO. Aquí se acepta el mensaje entrante como
 * base para UTILITY porque es lo que hace el producto usable hoy; la decisión es del
 * dueño del negocio y puede endurecerse cambiando esta función. Si prospera el proyecto
 * que se sigue en POST-LANZAMIENTO, dejará de ser decisión y pasará a obligación.
 *
 * Y ESTE REGISTRO ES NECESARIO PERO NO SUFICIENTE: Meta tiene la última palabra. Una
 * persona puede desactivar «Ofertas y anuncios» en el perfil del negocio dentro de
 * WhatsApp, o dar pulgar abajo, y eso ocurre en la app SIN webhook. Nuestro registro
 * diría «consintió» y Meta estaría bloqueando igual. No es arreglable desde aquí: un
 * `consentimiento: 'si'` nuestro no garantiza la entrega.
 *
 * NO HAY CADUCIDAD DEL HECHO, Y ES A PROPÓSITO. Una clínica dental contactando a un
 * paciente un año después es normal; un e-commerce promocionando a quien escribió hace
 * dos años, no. La ventana razonable depende del negocio, no del software: fijar N
 * meses aquí sería PUX decidiendo por el dueño del negocio, el mismo error que el
 * booleano de opt-in que se descartó. Lo que sí se hace es DEVOLVER la antigüedad
 * (`antiguedadDias`) para que la UI la muestre antes de lanzar y el operador decida con
 * el dato delante. Agrupar por antigüedad es presentación; no filtra.
 */

const CATEGORIAS_CONOCIDAS = new Set(['UTILITY', 'MARKETING', 'AUTHENTICATION']);

/** Códigos estables para el frontend. El texto legible se compone arriba. */
/**
 * LA BASE JURÍDICA DEL ENVÍO — el tercer concepto que pidió el abogado.
 *
 *   autorización/consentimiento  !=  base jurídica del tratamiento  !=  evidencia
 *
 * Hasta el 31-ago-2026 esto se calculaba al vuelo y se tiraba: `puedeRecibir`
 * devolvía un booleano y nada quedaba escrito de POR QUÉ se permitió un envío. Con
 * el ledger se puede reconstruir qué CONSTABA en una fecha, pero no qué base
 * SUSTENTÓ aquel mensaje ni cuál necesitaba — que es la pregunta que hace un
 * auditor.
 *
 * LAS TRES NO VALEN LO MISMO, y por eso son tres y no un booleano:
 *
 *   · CONSENTIMIENTO_PUBLICITARIO — el más fuerte y el más exigente. Sub-distinguido
 *     por `origen`: uno evidenciado (la persona respondió OFERTAS, con dos wamid
 *     verificables contra Meta) NO vale lo mismo ante un reclamo que uno declarado
 *     por el negocio sin evidencia que PUX pueda ver.
 *   · CONSENTIMIENTO_DIFUSION — el negocio declara tener autorización para mensajes
 *     de servicio. Tampoco lleva evidencia verificable.
 *   · INTERACCION_PREVIA — la persona escribió al negocio. **NO ES CONSENTIMIENTO**,
 *     y el abogado lo dijo expresamente (bloque B2): «puede respaldar determinados
 *     tratamientos necesarios para atender su solicitud, pero no constituye
 *     automáticamente consentimiento para marketing». Mezclarla con las otras dos en
 *     el mismo `return` era exactamente lo que impedía distinguirlas.
 */
const BASES = {
  CONSENTIMIENTO_PUBLICITARIO: 'consentimiento_publicitario',
  CONSENTIMIENTO_DIFUSION: 'consentimiento_difusion',
  INTERACCION_PREVIA: 'interaccion_previa',
  // UNA FINALIDAD ESPECÍFICA aceptada por la persona (5-nov-2026: el «Sí, avísame» de la invitación a eventos de
  // L&G, `consentimiento.especificas.invitacionEventos`). Autoriza ESA comunicación y ninguna otra: sólo la usa quien
  // la pide por su nombre (`opciones.especifica`), y eso lo pasa el servidor que envía la invitación, nunca un
  // campo del documento de la campaña, que cualquier miembro puede escribir.
  CONSENTIMIENTO_ESPECIFICO: 'consentimiento_especifico',
};

const MOTIVOS = {
  CATEGORIA_DESCONOCIDA: 'categoria_desconocida',
  CATEGORIA_NO_ENVIABLE: 'categoria_no_enviable',
  SIN_MENSAJE_PREVIO: 'sin_mensaje_previo',
  SIN_CONSENTIMIENTO: 'sin_consentimiento',
  CONSENTIMIENTO_RETIRADO: 'consentimiento_retirado',
  // NO ES LO MISMO QUE EL ANTERIOR, y por eso es un motivo aparte: aquí NADIE
  // RETIRÓ NADA. El negocio importó el contacto y dejó sin marcar la casilla de
  // mensajes de servicio, así que no declara tener autorización para ellos.
  // Mostrar «consentimiento retirado» en este caso sería mentirle al operador
  // sobre por qué no puede enviar, y mandarle a buscar una baja que no existe.
  DIFUSION_NO_DECLARADA: 'difusion_no_declarada',
  SIN_CONSENTIMIENTO_ESPECIFICO: 'sin_consentimiento_especifico',
};

/** Texto en claro, para que el motivo llegue legible a los resultados y a la UI. */
const TEXTO_MOTIVO = {
  [MOTIVOS.DIFUSION_NO_DECLARADA]:
    'El negocio no declaró tener autorización para enviarle mensajes de servicio a este contacto.',
  [MOTIVOS.CATEGORIA_DESCONOCIDA]:
    'No se sabe la categoría de la plantilla, así que no se puede aplicar la política de consentimiento.',
  [MOTIVOS.CATEGORIA_NO_ENVIABLE]:
    'Los códigos de verificación se envían uno a uno, no en campañas.',
  [MOTIVOS.SIN_MENSAJE_PREVIO]:
    'No consta que esta persona haya escrito al negocio.',
  [MOTIVOS.SIN_CONSENTIMIENTO]:
    'Las plantillas de marketing requieren consentimiento explícito y no consta.',
  [MOTIVOS.CONSENTIMIENTO_RETIRADO]:
    'Esta persona pidió no recibir más mensajes.',
  [MOTIVOS.SIN_CONSENTIMIENTO_ESPECIFICO]:
    'Esta campaña requiere que la persona haya aceptado expresamente este aviso, y no consta.',
};

/** `true` si la categoría puede enviarse desde campañas (aunque el destinatario no pueda). */
function categoriaEnviableEnCampanas(categoria) {
  return categoria === 'UTILITY' || categoria === 'MARKETING';
}

/** Días transcurridos desde el hecho, o `null` si no hay hecho ni referencia fiable. */
function antiguedadEnDias(primerMensajeEntrante, ahoraMs) {
  const fecha = primerMensajeEntrante?.fecha;
  const ms = typeof fecha?.toMillis === 'function'
    ? fecha.toMillis()
    : (fecha?._seconds !== undefined ? fecha._seconds * 1000 : null);
  if (ms === null || !Number.isFinite(ms)) return null;
  return Math.floor((ahoraMs - ms) / 86400000);
}

/** Milisegundos de una `fecha` de consentimiento (Timestamp, {_seconds}, Date, número), o null. */
function msDeFecha(fecha) {
  if (fecha === null || fecha === undefined) return null;
  if (typeof fecha.toMillis === 'function') return fecha.toMillis();
  if (typeof fecha._seconds === 'number') return fecha._seconds * 1000;
  if (fecha instanceof Date) return fecha.getTime();
  if (typeof fecha === 'number') return fecha;
  return null;
}

/**
 * ¿Se le puede enviar ESTA categoría a ESTE contacto?
 *
 * @param {string|null} categoria  `templateCategory` de la campaña. `null` en las
 *   campañas creadas antes de que el dato existiera: se trata como desconocida y se
 *   bloquea. Ausente no es UTILITY.
 * @param {object|null} contacto   el doc del chat: `{primerMensajeEntrante, consentimiento}`
 * @param {number} [ahoraMs]       inyectable para que el arnés no dependa del reloj
 * @param {{especifica?: string|null}} [opciones]  `especifica`: la MARKETING sale por esa finalidad específica
 *   aceptada (`consentimiento.especificas[nombre]`) en vez de por el consentimiento publicitario. Sólo la pasa el
 *   servidor (ver `BASES.CONSENTIMIENTO_ESPECIFICO`).
 * @returns {{permitido: boolean, motivo?: string, mensaje?: string, antiguedadDias: number|null}}
 */
function puedeRecibir(categoria, contacto, ahoraMs = Date.now(), { especifica = null } = {}) {
  const antiguedadDias = antiguedadEnDias(contacto?.primerMensajeEntrante, ahoraMs);
  const no = (motivo) => ({ permitido: false, motivo, mensaje: TEXTO_MOTIVO[motivo], antiguedadDias });

  /**
   * SE DEVUELVE LA BASE APLICADA **Y** LAS DISPONIBLES.
   *
   * Si mañana se impugna una —«ese consentimiento declarado no valía»— la otra sigue
   * sosteniendo el envío. Con sólo la aplicada, impugnarla dejaría el mensaje sin
   * base aunque hubiera otra perfectamente válida en el momento de enviarlo.
   *
   * LA INSTANTÁNEA VA COMPLETA, no sólo un puntero al asiento. `INTERACCION_PREVIA`
   * NO TIENE ASIENTO —`primerMensajeEntrante` vive en el documento del chat, no en el
   * ledger— así que con sólo el puntero esa base quedaría sin registrar, y es justo la
   * que el abogado señaló como distinta del consentimiento. El puntero se añade cuando
   * existe, para poder cotejar el registro contra el ledger.
   */
  const si = (tipo, disponibles) => {
    const c = contacto?.consentimiento || {};
    const fuente = tipo === BASES.CONSENTIMIENTO_PUBLICITARIO ? c.publicidad
      : tipo === BASES.CONSENTIMIENTO_DIFUSION ? c.difusion
        : tipo === BASES.CONSENTIMIENTO_ESPECIFICO ? c.especificas?.[especifica]
          : null;
    return {
      permitido: true,
      antiguedadDias,
      base: {
        tipo,
        origen: fuente?.origen ?? (tipo === BASES.INTERACCION_PREVIA ? 'mensaje_entrante' : null),
        // El asiento del ledger que la sustenta. `null` EXPLÍCITO cuando la vía aún no
        // deja asiento, con el motivo al lado: un auditor tiene que ver que FALTA, no
        // concluir que no lo hubo.
        eventoId: fuente?.eventoId ?? null,
        motivoSinAsiento: fuente?.eventoId ? null
          : (tipo === BASES.INTERACCION_PREVIA ? 'hecho_no_es_asiento' : 'via_no_migrada'),
        evidencia: fuente?.evidencia
          ?? (tipo === BASES.INTERACCION_PREVIA
            ? (contacto?.primerMensajeEntrante?.evidencia ?? null) : null),
        fecha: fuente?.fecha ?? contacto?.primerMensajeEntrante?.fecha ?? null,
      },
      basesDisponibles: disponibles,
    };
  };

  if (typeof categoria !== 'string' || !CATEGORIAS_CONOCIDAS.has(categoria)) {
    return no(MOTIVOS.CATEGORIA_DESCONOCIDA);
  }
  if (!categoriaEnviableEnCampanas(categoria)) {
    return no(MOTIVOS.CATEGORIA_NO_ENVIABLE);
  }

  // DOS FINALIDADES, NO UNA. La Ley 21.719 exige que el consentimiento sea «específico
  // en cuanto a su finalidad o finalidades»: un único «sí a todo» es justo lo que
  // rechaza. Por eso `difusion` y `publicidad` se guardan y se leen por separado.
  //
  //   difusion    ¿se le puede escribir en frío, con cualquier plantilla?
  //   publicidad  ¿se le pueden mandar promociones?
  //
  // Una baja de difusión manda sobre todo lo demás, incluida una UTILITY con mensaje
  // previo: la persona pidió no recibir más.
  //
  // ═════════════════════════════════════════════════════════════════════════════
  //  PERO SÓLO EL DESTINATARIO REVOCA. Un `difusion.estado === 'no'` tiene dos
  //  orígenes que NO significan lo mismo:
  //
  //    origen 'palabra_clave'            -> la persona escribió BAJA. Se opuso.
  //    origen 'declarado_por_el_negocio' -> el operador vio la casilla de mensajes
  //                                         de servicio y no la marcó. No declara
  //                                         tener autorización. NADIE SE OPUSO.
  //
  //  La distinción no hacía falta hasta el 31-ago-2026 porque el segundo caso NO
  //  EXISTÍA: la declaración por CSV BORRABA la finalidad no marcada en vez de
  //  negarla, y ausente ya significaba desconocido. El ledger de consentimiento
  //  la hace existir, porque un asiento tiene que decir qué se pronunció.
  //
  //  SIN ESTO SE ROMPE UN CASO REAL Y MEDIDO: un negocio que declara SÓLO
  //  «mensajes promocionales» produciría `difusion:'no'`, y esta guarda bloquearía
  //  TAMBIÉN el marketing que acaba de autorizar — con el motivo «consentimiento
  //  retirado», que además es falso.
  //
  //  Es el tercer concepto que pidió el abogado apareciendo antes de lo previsto:
  //  autorización != base jurídica != evidencia. Ver CONSULTAS-LEGALES.md.
  // ═════════════════════════════════════════════════════════════════════════════
  const difusion = contacto?.consentimiento?.difusion;
  const revocadaPorLaPersona = difusion?.estado === 'no'
    && difusion?.origen !== 'declarado_por_el_negocio';
  if (revocadaPorLaPersona) {
    return no(MOTIVOS.CONSENTIMIENTO_RETIRADO);
  }

  if (categoria === 'MARKETING' && especifica) {
    // LA REGLA DE LA INVITACIÓN (Rodrigo, 4-nov-2026): la específica aceptada («Sí, avísame»), sin una BAJA
    // posterior y sin «No, gracias». La BAJA de difusión ya cortó arriba, sea cual sea su fecha. Aquí: la
    // específica tiene que decir 'si' (un 'no' es el «No, gracias»), y una publicidad retirada por la persona
    // DESPUÉS de aceptarla, o sin fechas con que ordenarlas, también corta: ante la duda, no se envía.
    const e = contacto?.consentimiento?.especificas?.[especifica];
    if (e?.estado !== 'si') return no(MOTIVOS.SIN_CONSENTIMIENTO_ESPECIFICO);
    const pub = contacto?.consentimiento?.publicidad;
    if (pub?.estado === 'no' && pub?.origen !== 'declarado_por_el_negocio') {
      const msPub = msDeFecha(pub.fecha);
      const msEsp = msDeFecha(e.fecha);
      if (msPub === null || msEsp === null || msPub >= msEsp) return no(MOTIVOS.CONSENTIMIENTO_RETIRADO);
    }
    return si(BASES.CONSENTIMIENTO_ESPECIFICO, [BASES.CONSENTIMIENTO_ESPECIFICO]);
  }

  if (categoria === 'MARKETING') {
    // Explícito quiere decir explícito: cualquier otro estado —ausente, 'desconocido',
    // lo que sea— NO es un sí. Y `difusion` no vale como sustituto: no haberse dado de
    // baja no es haber consentido publicidad.
    if (contacto?.consentimiento?.publicidad?.estado !== 'si') return no(MOTIVOS.SIN_CONSENTIMIENTO);
    // Para MARKETING sólo hay UNA base posible: el consentimiento publicitario. Ni la
    // interacción previa ni la difusión valen — el abogado y la ley coinciden en que
    // escribir al negocio no autoriza publicidad.
    return si(BASES.CONSENTIMIENTO_PUBLICITARIO, [BASES.CONSENTIMIENTO_PUBLICITARIO]);
  }

  // UTILITY · basta con UNA de las dos bases:
  //
  //   · consta que la persona escribió al negocio (`primerMensajeEntrante`), o
  //   · el negocio DECLARÓ tener su consentimiento (`difusion.estado === 'si'`).
  //
  // La segunda es lo que hace usable la importación por CSV: un contacto que nunca
  // escribió no tiene hecho, y sin esta rama quedaría bloqueado para siempre aunque
  // el negocio tenga un formulario firmado que PUX no puede ver.
  //
  // Y la declaración cubre las DOS finalidades a propósito: restringirla a marketing
  // sería arbitrario — la misma persona que no podría recibir una promoción tampoco
  // podría recibir un recordatorio, y el consentimiento que el negocio afirma tener
  // cubre ambas.
  // EL 'no' DEL NEGOCIO BLOQUEA AQUÍ, no arriba, y con su motivo verdadero: no hay
  // base para una UTILITY si el negocio no declara autorización de difusión y la
  // persona tampoco escribió. Bloquea la UTILITY y NO el marketing, que es la
  // diferencia con una baja.
  if (difusion?.estado === 'no') {
    return no(MOTIVOS.DIFUSION_NO_DECLARADA);
  }

  const declaroDifusion = difusion?.estado === 'si';
  const hayInteraccion = !!contacto?.primerMensajeEntrante;
  if (!hayInteraccion && !declaroDifusion) {
    return no(MOTIVOS.SIN_MENSAJE_PREVIO);
  }

  // LAS DOS PUEDEN DARSE A LA VEZ. Se aplica la declaración cuando existe —es una
  // autorización, y la interacción previa NO lo es— pero se registran las dos: si
  // mañana se impugna la declaración, la interacción sigue sosteniendo el envío.
  const disponibles = [
    ...(declaroDifusion ? [BASES.CONSENTIMIENTO_DIFUSION] : []),
    ...(hayInteraccion ? [BASES.INTERACCION_PREVIA] : []),
  ];
  return si(declaroDifusion ? BASES.CONSENTIMIENTO_DIFUSION : BASES.INTERACCION_PREVIA, disponibles);
}

module.exports = {
  CATEGORIAS_CONOCIDAS,
  BASES,
  MOTIVOS,
  TEXTO_MOTIVO,
  categoriaEnviableEnCampanas,
  antiguedadEnDias,
  puedeRecibir,
};
